import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowLeft, ShieldCheck, Cloud, Brain, Award, Quote } from "lucide-react";
import { motion } from "framer-motion";
import { SITE } from "@/lib/site";

/* ───────────────────────── MIDNIGHT TECH PREVIEW ───────────────────────── */

export default function MidnightPreview() {
  return (
    <div className="min-h-screen text-white" style={{ background: "#050505" }} data-testid="preview-midnight">
      <ThemeBar variant="midnight" />

      <section
        className="relative pt-24 pb-16 md:pb-20 overflow-hidden"
        style={{
          background:
            "radial-gradient(1200px 600px at 80% -10%, rgba(37,99,235,0.16), transparent 60%), radial-gradient(900px 500px at -10% 30%, rgba(6,182,212,0.10), transparent 60%), #050505",
        }}
      >
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
            backgroundSize: "56px 56px",
            maskImage: "radial-gradient(ellipse at center, #000 50%, transparent 80%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7" style={{ fontFamily: "Satoshi, sans-serif" }}>
            <div className="flex items-center gap-3 mb-6 font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500">
              <span className="h-px w-8 bg-blue-500" />
              Corporate Trainer · Cloud Architect · AI Security
            </div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
              className="font-extrabold tracking-tighter leading-[0.95] text-5xl sm:text-6xl md:text-7xl"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              <span className="block">Engineering trust</span>
              <span className="block" style={{
                backgroundImage: "linear-gradient(90deg, #fff 0%, #93c5fd 50%, #67e8f9 100%)",
                WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
              }}>at the speed of cloud.</span>
            </motion.h1>
            <p className="mt-7 max-w-xl text-base md:text-lg text-slate-400 leading-relaxed">
              I'm <span className="text-white font-medium">Ajay Sahni</span> — a senior corporate trainer and consultant with 20+ years in IT and 15+ years running Fortune-500 enterprise programs across Microsoft, Cloud, Cybersecurity and AI Security.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <button className="inline-flex items-center justify-center gap-2 bg-white text-black font-medium px-7 py-4 rounded-full hover:bg-slate-200 transition-colors">
                Book a discovery call <ArrowUpRight size={18} />
              </button>
              <button className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full border border-white/15 text-white hover:bg-white/5 transition-colors">
                Explore programs
              </button>
            </div>
          </div>

          <motion.div initial={{ opacity: 0, scale: 1.04 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.1 }} className="lg:col-span-5 relative">
            <div className="relative aspect-[4/5] max-w-md mx-auto">
              <div className="absolute -inset-4 rounded-[2rem] blur-2xl" style={{ background: "linear-gradient(135deg, rgba(37,99,235,0.25), transparent, rgba(103,232,249,0.25))" }} />
              <div className="relative h-full w-full overflow-hidden rounded-[1.75rem] border border-white/10" style={{ boxShadow: "0 0 0 1px rgba(59,130,246,0.30), 0 10px 40px -10px rgba(37,99,235,0.55)" }}>
                <img src={SITE.portrait} alt="Ajay Sahni" className="h-full w-full object-cover object-top" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <div className="absolute bottom-5 left-5 right-5">
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-blue-300">{SITE.domain}</div>
                  <div className="font-bold text-white text-lg mt-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Ajay Sahni</div>
                  <div className="text-xs text-slate-300 mt-0.5">{SITE.tagline}</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-14 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 rounded-2xl overflow-hidden">
          {[["20+","Years in IT"],["15+","Years training"],["10,000+","Pros trained"],["50+","Enterprise clients"]].map(([v,l]) => (
            <div key={l} className="p-6 md:p-8" style={{ background: "#0A0C13" }}>
              <div className="font-extrabold text-3xl md:text-4xl tracking-tighter text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{v}</div>
              <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">{l}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex items-center gap-3 mb-6 font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500">
            <span className="h-px w-8 bg-white/30" />01 — Expertise
          </div>
          <h2 className="font-bold text-3xl sm:text-4xl md:text-5xl tracking-tight text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Seven disciplines. <span className="text-slate-500">One coherent practice.</span>
          </h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { icon: Cloud, t: "Cloud Architecture", b: "Azure · AWS · GCP · Landing Zones" },
              { icon: ShieldCheck, t: "Cybersecurity", b: "CISSP · CCSP · CEH · Security+" },
              { icon: Brain, t: "AI Security", b: "GenAI · LLM Red-Team · Copilot" },
            ].map((c) => (
              <div key={c.t} className="bg-[#0A0C13] border border-white/10 rounded-2xl p-7 hover:border-white/25 transition-colors">
                <div className="h-11 w-11 rounded-lg grid place-items-center" style={{ background: "rgba(37,99,235,0.15)", border: "1px solid rgba(37,99,235,0.30)", color: "#60a5fa" }}>
                  <c.icon size={20} />
                </div>
                <div className="mt-5 font-semibold text-xl text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{c.t}</div>
                <div className="mt-2 text-sm text-slate-400">{c.b}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PreviewFooter variant="midnight" />
    </div>
  );
}

/* ─── Reusable theme switch bar + footer ─── */
export function ThemeBar({ variant }) {
  const styles = {
    midnight: { bg: "#050505", text: "#fff", border: "rgba(255,255,255,0.10)" },
    editorial: { bg: "#FAF7F2", text: "#0B1F3A", border: "rgba(11,31,58,0.15)" },
    cyber: { bg: "#000", text: "#00FF7F", border: "rgba(0,255,127,0.30)" },
  };
  const s = styles[variant];
  return (
    <div
      className="border-b"
      style={{ background: s.bg, color: s.text, borderColor: s.border }}
      data-testid="theme-bar"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-12 flex items-center justify-between text-xs">
        <Link to="/themes" className="inline-flex items-center gap-2 opacity-70 hover:opacity-100">
          <ArrowLeft size={12} /> All themes
        </Link>
        <div className="font-mono uppercase tracking-[0.22em] text-[10px]">
          Preview · {variant} · aksahni.com
        </div>
        <div className="flex items-center gap-3">
          <Link to="/preview/midnight" className={variant === "midnight" ? "underline underline-offset-4" : "opacity-50 hover:opacity-100"}>Midnight</Link>
          <Link to="/preview/editorial" className={variant === "editorial" ? "underline underline-offset-4" : "opacity-50 hover:opacity-100"}>Editorial</Link>
          <Link to="/preview/cyber" className={variant === "cyber" ? "underline underline-offset-4" : "opacity-50 hover:opacity-100"}>Cyber</Link>
        </div>
      </div>
    </div>
  );
}

export function PreviewFooter({ variant }) {
  const styles = {
    midnight: { bg: "#050505", text: "#94a3b8", border: "rgba(255,255,255,0.08)" },
    editorial: { bg: "#FAF7F2", text: "#475569", border: "rgba(11,31,58,0.15)" },
    cyber: { bg: "#000", text: "#65D973", border: "rgba(0,255,127,0.30)" },
  };
  const s = styles[variant];
  return (
    <footer className="border-t mt-8" style={{ background: s.bg, color: s.text, borderColor: s.border }}>
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 text-xs flex items-center justify-between">
        <div>© {new Date().getFullYear()} Ajay Sahni · aksahni.com</div>
        <Link to="/themes" className="underline underline-offset-4">Choose this theme →</Link>
      </div>
    </footer>
  );
}
